/**
 * Irrigation Season Redux Slice
 *
 * Manages state for irrigation season data including seasons, allocation status,
 * water tracking, season summary, and active season management in Redux store.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  IrrigationSeason,
  AllocationStatus,
  WaterAllocationTracking,
  SeasonSummary,
  ListSeasonsParams,
  ListSeasonsResponse,
  ListTrackingParams,
  ListTrackingResponse,
  ActiveSeasonResponse,
  CreateSeasonRequest,
  UpdateSeasonRequest,
  CheckAllocationResponse,
} from '@/types/irrigationSeason';
import irrigationSeasonApi from '@/services/irrigationSeasonApi';

// ============================================================================
// Irrigation Season State Interface
// ============================================================================

interface IrrigationSeasonState {
  seasons: IrrigationSeason[];
  currentSeason: IrrigationSeason | null;
  activeSeason: IrrigationSeason | null;
  allocationStatus: AllocationStatus | null;
  seasonSummary: SeasonSummary | null;
  waterTracking: ListTrackingResponse | null;
  loading: boolean;
  error: string | null;
  /** Dedupe key of the fetchActiveSeason request currently in flight, so
   *  many components mounting at once (e.g. one status card per sector)
   *  collapse into a single HTTP request instead of N identical ones. */
  activeSeasonInFlightKey: string | null;
}

const initialIrrigationSeasonState: IrrigationSeasonState = {
  seasons: [],
  currentSeason: null,
  activeSeason: null,
  allocationStatus: null,
  seasonSummary: null,
  waterTracking: null,
  loading: false,
  error: null,
  activeSeasonInFlightKey: null,
};

// ============================================================================
// Async Thunks - Season Management
// ============================================================================

/**
 * Fetch seasons for a farm with pagination and filtering.
 */
export const fetchSeasons = createAsyncThunk(
  'irrigationSeason/fetchSeasons',
  async ({ farmId, params }: { farmId: string; params?: ListSeasonsParams }) => {
    const response = await irrigationSeasonApi.getSeasons(farmId, params);
    return response;
  }
);

/**
 * Fetch a specific irrigation season by ID.
 */
export const fetchSeason = createAsyncThunk(
  'irrigationSeason/fetchSeason',
  async (seasonId: number) => {
    const response = await irrigationSeasonApi.getSeason(seasonId);
    return response;
  }
);

/**
 * Create a new irrigation season for a farm.
 */
export const createSeason = createAsyncThunk(
  'irrigationSeason/createSeason',
  async ({ farmId, data }: { farmId: string; data: CreateSeasonRequest }) => {
    const response = await irrigationSeasonApi.createSeason(farmId, data);
    return response;
  }
);

/**
 * Update an existing irrigation season.
 */
export const updateSeason = createAsyncThunk(
  'irrigationSeason/updateSeason',
  async ({ seasonId, data }: { seasonId: number; data: UpdateSeasonRequest }) => {
    const response = await irrigationSeasonApi.updateSeason(seasonId, data);
    return response;
  }
);

/**
 * Close an irrigation season.
 */
export const closeSeason = createAsyncThunk(
  'irrigationSeason/closeSeason',
  async ({ seasonId, endDate }: { seasonId: number; endDate?: string }) => {
    const response = await irrigationSeasonApi.closeSeason(seasonId, endDate);
    return response;
  }
);

/**
 * Permanently delete an irrigation season.
 */
export const deleteSeason = createAsyncThunk(
  'irrigationSeason/deleteSeason',
  async (seasonId: number) => {
    await irrigationSeasonApi.deleteSeason(seasonId);
    return seasonId;
  }
);

// ============================================================================
// Async Thunks - Water Allocation
// ============================================================================

/**
 * Fetch allocation status for a season.
 */
export const fetchAllocationStatus = createAsyncThunk(
  'irrigationSeason/fetchAllocationStatus',
  async (seasonId: number) => {
    const response = await irrigationSeasonApi.getAllocationStatus(seasonId);
    return response;
  }
);

/**
 * Fetch season summary including status, allocation, and tracking.
 */
export const fetchSeasonSummary = createAsyncThunk(
  'irrigationSeason/fetchSeasonSummary',
  async ({ seasonId, speciesId }: { seasonId: number; speciesId?: number }) => {
    const response = await irrigationSeasonApi.getSeasonSummary(seasonId, speciesId);
    return response;
  }
);

/**
 * Fetch water tracking records for a season.
 */
export const fetchWaterTracking = createAsyncThunk(
  'irrigationSeason/fetchWaterTracking',
  async ({ seasonId, params }: { seasonId: number; params?: ListTrackingParams }) => {
    const response = await irrigationSeasonApi.getWaterTracking(seasonId, params);
    return response;
  }
);

/**
 * Record daily water usage for a season.
 */
export const recordWaterUsage = createAsyncThunk(
  'irrigationSeason/recordWaterUsage',
  async ({ seasonId, data }: { seasonId: number; data: { date: string; irrigation_m3: number } }) => {
    const response = await irrigationSeasonApi.recordWaterUsage(seasonId, data);
    return response;
  }
);

/**
 * Check allocation limit before recording water usage.
 */
export const checkAllocation = createAsyncThunk(
  'irrigationSeason/checkAllocation',
  async ({ seasonId, additionalM3 }: { seasonId: number; additionalM3: number }) => {
    const response = await irrigationSeasonApi.checkAllocation(seasonId, additionalM3);
    return response;
  }
);

// ============================================================================
// Async Thunks - Farm-Level
// ============================================================================

/**
 * Fetch active season for a farm.
 */
const activeSeasonKey = (farmId: string, speciesId?: number) =>
  `${farmId}|${speciesId ?? ''}`;

export const fetchActiveSeason = createAsyncThunk(
  'irrigationSeason/fetchActiveSeason',
  async ({ farmId, speciesId }: { farmId: string; speciesId?: number }) => {
    const response = await irrigationSeasonApi.getActiveSeason(farmId, speciesId);
    return response;
  },
  {
    // Collapse identical concurrent requests: when dozens of sector cards
    // mount together they all dispatch this thunk; only the first hits the
    // network, the rest read the shared store entry when it resolves.
    // Completed requests are never blocked, so post-mutation refetches work.
    condition: ({ farmId, speciesId }, { getState }) => {
      const st = (getState() as { irrigationSeason: IrrigationSeasonState }).irrigationSeason;
      return st.activeSeasonInFlightKey !== activeSeasonKey(farmId, speciesId);
    },
  }
);

// ============================================================================
// Slice
// ============================================================================

const irrigationSeasonSlice = createSlice({
  name: 'irrigationSeason',
  initialState: initialIrrigationSeasonState,
  reducers: {
    setCurrentSeason: (state, action: PayloadAction<IrrigationSeason | null>) => {
      state.currentSeason = action.payload;
    },
    clearCurrentSeason: (state) => {
      state.currentSeason = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Seasons
    builder
      .addCase(fetchSeasons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSeasons.fulfilled, (state, action) => {
        state.loading = false;
        state.seasons = action.payload.items;
        // DIAGNOSTIC LOG: Log fetched seasons
        console.log('[DIAGNOSTIC] fetchSeasons.fulfilled - seasons received:', {
          total: action.payload.total,
          items_count: action.payload.items.length,
          items: action.payload.items.map((s: any) => ({
            id: s.id,
            farm_id: s.farm_id,
            species_id: s.species_id,
            year: s.year,
            is_active: s.is_active,
          })),
        });
      })
      .addCase(fetchSeasons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch seasons';
      });

    // Fetch Season
    builder
      .addCase(fetchSeason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSeason.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSeason = action.payload;
      })
      .addCase(fetchSeason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch season';
      });

    // Create Season
    builder
      .addCase(createSeason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSeason.fulfilled, (state, action) => {
        state.loading = false;
        state.seasons.push(action.payload);
        state.currentSeason = action.payload;
      })
      .addCase(createSeason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create season';
      });

    // Update Season
    builder
      .addCase(updateSeason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSeason.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.seasons.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.seasons[index] = action.payload;
        }
        if (state.currentSeason?.id === action.payload.id) {
          state.currentSeason = action.payload;
        }
      })
      .addCase(updateSeason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update season';
      });

    // Close Season
    builder
      .addCase(closeSeason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(closeSeason.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.seasons.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.seasons[index] = action.payload;
        }
        if (state.currentSeason?.id === action.payload.id) {
          state.currentSeason = action.payload;
        }
      })
      .addCase(closeSeason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to close season';
      });

    // Delete Season
    builder
      .addCase(deleteSeason.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSeason.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.seasons = state.seasons.filter((s) => s.id !== deletedId);
        if (state.currentSeason?.id === deletedId) {
          state.currentSeason = null;
        }
        if (state.activeSeason?.id === deletedId) {
          state.activeSeason = null;
        }
      })
      .addCase(deleteSeason.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete season';
      });

    // Fetch Allocation Status
    builder
      .addCase(fetchAllocationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllocationStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.allocationStatus = action.payload;
      })
      .addCase(fetchAllocationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch allocation status';
      });

    // Fetch Season Summary
    builder
      .addCase(fetchSeasonSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSeasonSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.seasonSummary = action.payload;
      })
      .addCase(fetchSeasonSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch season summary';
      });

    // Fetch Water Tracking
    builder
      .addCase(fetchWaterTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.waterTracking = action.payload;
      })
      .addCase(fetchWaterTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch water tracking';
      });

    // Record Water Usage
    builder
      .addCase(recordWaterUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordWaterUsage.fulfilled, (state, action) => {
        state.loading = false;
        if (state.waterTracking) {
          state.waterTracking.items.push(action.payload);
        }
      })
      .addCase(recordWaterUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to record water usage';
      });

    // Check Allocation
    builder
      .addCase(checkAllocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAllocation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkAllocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to check allocation';
      });

    // Fetch Active Season
    builder
      .addCase(fetchActiveSeason.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.activeSeasonInFlightKey = activeSeasonKey(
          action.meta.arg.farmId,
          action.meta.arg.speciesId,
        );
      })
      .addCase(fetchActiveSeason.fulfilled, (state, action) => {
        state.loading = false;
        state.activeSeason = action.payload.season;
        state.activeSeasonInFlightKey = null;
      })
      .addCase(fetchActiveSeason.rejected, (state, action) => {
        state.loading = false;
        state.activeSeasonInFlightKey = null;
        // condition() rejections are silent dedupe hits, not failures.
        if (!action.meta.condition) {
          state.error = action.error.message || 'Failed to fetch active season';
        }
      });
  },
});

export const { setCurrentSeason, clearCurrentSeason, clearError } = irrigationSeasonSlice.actions;
export const irrigationSeasonReducer = irrigationSeasonSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectSeasons = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.seasons;

export const selectCurrentSeason = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.currentSeason;

export const selectActiveSeason = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.activeSeason;

export const selectAllocationStatus = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.allocationStatus;

export const selectSeasonSummary = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.seasonSummary;

export const selectWaterTracking = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.waterTracking;

export const selectIrrigationSeasonLoading = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.loading;

export const selectIrrigationSeasonError = (state: { irrigationSeason: IrrigationSeasonState }) =>
  state.irrigationSeason.error;
